import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Search, TrendingUp, AlertTriangle, CheckCircle2, XCircle, ChevronRight, ChevronDown, ArrowUp, ArrowDown, ArrowUpDown, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { exportStyledXlsx } from "../lib/excelExport";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v) || 0;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0, style: "currency", currency: "INR" });
};

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  AT_RISK: "bg-amber-50 text-amber-700 border-amber-200",
  INACTIVE: "bg-gray-50 text-gray-600 border-gray-200",
};

/**
 * SIP / STP / SWP Book — book size is computed AS OF the previous calendar
 * month, the canonical "what billed last month" definition. Each row is a
 * client; metrics roll up across folios + schemes. Installment counter
 * (`X/Y`) is parsed from the `flag` field of transaction_master.
 */
export default function SystematicBookView({ type }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("active_book_size");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expanded, setExpanded] = useState({}); // pan → bool
  const [downloading, setDownloading] = useState(false);

  // Download all clients + scheme drill-down respecting current filters.
  // Paginates through the API at page_size=500 and accumulates rows.
  const handleDownload = async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const PAGE_SIZE = 500;
      const allClients = [];
      let p = 1;
      let totalPagesLocal = 1;
      do {
        const qs = new URLSearchParams({
          type, page: String(p), page_size: String(PAGE_SIZE),
          sort_by: sortBy, sort_order: sortOrder,
        });
        if (debounced) qs.append("search", debounced);
        if (statusFilter !== "__all__") qs.append("status", statusFilter);
        const res = await fetch(`${API_URL}/api/clients-wise-summary/systematic?${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Fetch failed");
        const j = await res.json();
        (j.clients || []).forEach((c) => allClients.push(c));
        totalPagesLocal = j?.pagination?.total_pages || 1;
        p += 1;
      } while (p <= totalPagesLocal);

      if (allClients.length === 0) {
        toast.info(`No ${type} data to export`);
        return;
      }

      // Sheet 1 — one row per client.
      const clientsSheet = allClients.map((c, i) => ({
        "#": i + 1,
        "Client": c.name || "",
        "PAN": c.pan || "",
        "Associate Code": c.associate_code || "",
        "Associate Name": c.associate_name || "",
        "Status": c.status || "",
        "Active Mandates": c.active_mandates || 0,
        "At Risk Mandates": c.at_risk_mandates || 0,
        "Inactive Mandates": c.inactive_mandates || 0,
        "Active Book Size": Number(c.active_book_size || 0),
        "Prev Mo. Collection": Number(c.prev_month_successful_amount || 0),
        "Prev Mo. Successful Count": c.prev_month_successful_count || 0,
        "Prev Mo. Bounced": Number(c.prev_month_failed_amount || 0),
        "Prev Mo. Failed Count": c.prev_month_failed_count || 0,
        "Last Successful Date": c.last_successful_date || "",
        "Latest Installment": c.installment_current
          ? `${c.installment_current}${c.installment_total ? "/" + c.installment_total : ""}`
          : "",
        "Schemes": (c.schemes || []).length,
      }));

      // Sheet 2 — one row per (client, scheme). Always emitted so RMs see the
      // full mandate-level breakdown without manually expanding rows.
      const schemesSheet = [];
      let row = 0;
      allClients.forEach((c) => {
        (c.schemes || []).forEach((s) => {
          row += 1;
          schemesSheet.push({
            "#": row,
            "Client": c.name || "",
            "PAN": c.pan || "",
            "Associate Code": c.associate_code || "",
            "Scheme": s.scheme_name || "",
            "Status": s.status || "",
            "Mandate Amount": Number(s.mandate_amount || 0),
            "Active Mandates": s.active_mandates || 0,
            "At Risk Mandates": s.at_risk_mandates || 0,
            "Inactive Mandates": s.inactive_mandates || 0,
            "Folios": s.folio_count || 0,
            "Successful Count": s.successful_count || 0,
            "Failed Count": s.failed_count || 0,
            "Latest Installment": s.installment_current
              ? `${s.installment_current}${s.installment_total ? "/" + s.installment_total : ""}`
              : "",
            "Stamp Duty": Number(s.stamp_duty_total || 0),
            "STT": Number(s.stt_total || 0),
            "Prev Mo. Collected": Number(s.prev_month_successful_amount || 0),
            "Prev Mo. Successful Count": s.prev_month_successful_count || 0,
            "Prev Mo. Bounced": Number(s.prev_month_failed_amount || 0),
            "Prev Mo. Failed Count": s.prev_month_failed_count || 0,
            "Last Successful Date": s.last_successful_date || "",
          });
        });
      });

      const ts = new Date().toISOString().slice(0, 10);
      const fname = `${type}_Book_${ts}.xlsx`;
      exportStyledXlsx(
        [
          { name: `${type} Clients`, rows: clientsSheet },
          { name: `${type} Schemes`, rows: schemesSheet.length ? schemesSheet : [{ Note: "No scheme breakdown" }] },
        ],
        fname,
      );
      toast.success(
        `Exported ${clientsSheet.length.toLocaleString()} clients · ${schemesSheet.length.toLocaleString()} scheme rows`,
        { description: fname },
      );
    } catch (e) {
      toast.error(`Failed to export ${type} book`, { description: e?.message || "" });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page AND clear stale data on type change so the user doesn't see
  // SIP rows leaking into the STP/SWP table while the new fetch is in-flight.
  useEffect(() => {
    setPage(1);
    setData(null);
    setExpanded({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Reset page on filter change (without clearing data — keeps previous results
  // visible during the brief filter re-fetch).
  useEffect(() => { setPage(1); }, [debounced, statusFilter]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError("");
    const qs = new URLSearchParams({
      type,
      page: String(page),
      page_size: "100",
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    if (debounced) qs.append("search", debounced);
    if (statusFilter !== "__all__") qs.append("status", statusFilter);

    // Capture the request "type" at fetch start. If this in-flight call
    // resolves AFTER the user has already switched to a different book type,
    // we drop the response — prevents the "overlapping data" bug where SIP
    // results would overwrite STP/SWP after a tab switch.
    const requestType = type;
    const ctrl = new AbortController();

    fetch(`${API_URL}/api/clients-wise-summary/systematic?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
    })
      .then((r) => r.ok ? r.json() : r.json().then((j) => Promise.reject(j)))
      .then((d) => {
        // Bail if user has switched tabs in the meantime
        if (requestType !== type) return;
        setData(d);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        if (requestType !== type) return;
        setError(e?.detail || "Failed to load systematic book");
      })
      .finally(() => {
        if (requestType === type) setLoading(false);
      });

    return () => ctrl.abort();
  }, [type, page, debounced, statusFilter, sortBy, sortOrder, token]);

  const summary = data?.summary || {};
  const clients = data?.clients || [];
  const totalPages = data?.pagination?.total_pages || 1;

  const headerLabel = useMemo(() => {
    return { SIP: "SIP Book", STP: "STP Book", SWP: "SWP Book" }[type] || `${type} Book`;
  }, [type]);

  const onSort = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortOrder("desc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    return sortOrder === "asc"
      ? <ArrowUp className="h-3.5 w-3.5 ml-1 text-amber-700" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1 text-amber-700" />;
  };

  return (
    <div className="space-y-6" data-testid={`${type.toLowerCase()}-book-view`}>
      {/* Summary cards — previous month basis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-teal-700 mb-1">
              Active {type} Book Size
            </div>
            <div className="text-2xl font-bold text-teal-900" data-testid={`${type.toLowerCase()}-book-size`}>
              {fmtINR(summary.active_book_size)}
            </div>
            <div className="text-xs text-teal-600 mt-1">
              {summary.active_mandates ?? 0} active · {summary.at_risk_mandates ?? 0} at risk
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1">
              Previous Month Collection
            </div>
            <div className="text-2xl font-bold text-emerald-900">
              {fmtINR(summary.prev_month_successful_amount)}
            </div>
            <div className="text-xs text-emerald-600 mt-1 truncate">
              {data?.window?.from} → {data?.window?.to}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-rose-700 mb-1">
              Bounce / Failed (last month)
            </div>
            <div className="text-2xl font-bold text-rose-900">
              {fmtINR(summary.prev_month_failed_amount)}
            </div>
            <div className="text-xs text-rose-600 mt-1">
              Bounce rate: {summary.prev_month_bounce_rate ?? 0}%
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-indigo-700 mb-1">
              Total Clients
            </div>
            <div className="text-2xl font-bold text-indigo-900">
              {summary.total_clients ?? 0}
            </div>
            <div className="text-xs text-indigo-600 mt-1">
              {summary.inactive_mandates ?? 0} inactive mandates (history)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between gap-3 flex-wrap">
            <span className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600" />
              {headerLabel} · Client-Wise
            </span>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by client name or PAN…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 w-64 text-sm"
                  data-testid={`${type.toLowerCase()}-search`}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                data-testid={`${type.toLowerCase()}-status-filter`}
              >
                <option value="__all__">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="AT_RISK">At Risk</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-1.5"
                onClick={handleDownload}
                disabled={downloading || loading}
                data-testid={`${type.toLowerCase()}-download-xlsx`}
              >
                {downloading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Download className="h-3.5 w-3.5" />}
                Download
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 mb-3">
              {error}
            </div>
          )}
          {loading && !data ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading {type} book…
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-sm">
              No {type} clients in scope for the previous month.
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader className="bg-amber-50 sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="font-semibold w-8"></TableHead>
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("name")}>
                      <div className="flex items-center">Client<SortIcon col="name" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("associate_code")}>
                      <div className="flex items-center">Associate<SortIcon col="associate_code" /></div>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("active_mandates")}>
                      <div className="flex items-center justify-center">Active / At-risk / Inactive<SortIcon col="active_mandates" /></div>
                    </TableHead>
                    <TableHead className="text-center font-semibold">Installments (last)</TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("prev_month_successful_amount")}>
                      <div className="flex items-center justify-end">Prev Mo. Collection<SortIcon col="prev_month_successful_amount" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("prev_month_failed_amount")}>
                      <div className="flex items-center justify-end">Prev Mo. Bounced<SortIcon col="prev_month_failed_amount" /></div>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-amber-100 font-semibold" onClick={() => onSort("active_book_size")}>
                      <div className="flex items-center justify-end">Active Book Size<SortIcon col="active_book_size" /></div>
                    </TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((c, i) => {
                    const isOpen = !!expanded[c.pan];
                    const schemes = c.schemes || [];
                    return (
                    <React.Fragment key={`${type}-${c.pan}-${i}`}>
                    <TableRow
                      className="hover:bg-amber-50/40 cursor-pointer"
                      onClick={() => setExpanded((e) => ({ ...e, [c.pan]: !e[c.pan] }))}
                      data-testid={`${type.toLowerCase()}-row-${i}`}
                    >
                      <TableCell className="p-1">
                        <button
                          type="button"
                          aria-label="Toggle schemes"
                          onClick={(ev) => { ev.stopPropagation(); setExpanded((e) => ({ ...e, [c.pan]: !e[c.pan] })); }}
                          className="p-1 rounded hover:bg-amber-100"
                          data-testid={`${type.toLowerCase()}-expand-${c.pan}`}
                        >
                          {isOpen
                            ? <ChevronDown className="h-4 w-4 text-amber-700" />
                            : <ChevronRight className="h-4 w-4 text-gray-500" />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-gray-500">{(page - 1) * 100 + i + 1}</TableCell>
                      <TableCell className="font-medium text-sm" title={`PAN: ${c.pan || "—"}`}>{c.name}</TableCell>
                      <TableCell>
                        {c.associate_code ? (
                          <span className="inline-flex items-center rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-mono text-teal-700" title={c.associate_name || ""}>
                            {c.associate_code}
                          </span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-emerald-600 font-semibold">{c.active_mandates}</span>
                          <span className="text-gray-300">·</span>
                          <span className="text-amber-600 font-semibold">{c.at_risk_mandates}</span>
                          <span className="text-gray-300">·</span>
                          <span className="text-gray-500">{c.inactive_mandates}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">
                        {c.installment_current
                          ? <span title="Latest successful installment counter">{c.installment_current}{c.installment_total ? `/${c.installment_total}` : ""}</span>
                          : <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell
                        className="text-right font-mono text-sm text-emerald-700"
                        title={c.last_successful_date ? `Last Success: ${c.last_successful_date}` : "No successful installment on record"}
                      >
                        {fmtINR(c.prev_month_successful_amount)}
                        <div className="text-[10px] text-emerald-500">{c.prev_month_successful_count} inst</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-rose-600">
                        {fmtINR(c.prev_month_failed_amount)}
                        <div className="text-[10px] text-rose-400">{c.prev_month_failed_count} bounced</div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-amber-800">
                        {fmtINR(c.active_book_size)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${STATUS_STYLES[c.status] || STATUS_STYLES.INACTIVE} text-[10px] uppercase tracking-wide`}>
                          {c.status === "ACTIVE" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {c.status === "AT_RISK" && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {c.status === "INACTIVE" && <XCircle className="h-3 w-3 mr-1" />}
                          {c.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-amber-50/30">
                        <TableCell colSpan={10} className="p-0">
                          <div className="px-8 py-4" data-testid={`${type.toLowerCase()}-drilldown-${c.pan}`}>
                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-3 font-semibold">
                              {schemes.length} Scheme{schemes.length === 1 ? "" : "s"} Breakdown for {c.name}
                            </div>
                            {schemes.length === 0 ? (
                              <div className="text-xs text-gray-500 py-2">No active scheme-level data.</div>
                            ) : (
                              <div className="border rounded-lg overflow-x-auto bg-white">
                                <Table>
                                  <TableHeader className="bg-gray-50">
                                    <TableRow>
                                      <TableHead className="font-semibold text-xs text-gray-600">Scheme</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-center">Successful Count</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-center">Failed Count</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-center">Last Installment</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-right">Mandate Amount</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-right" title="Stamp Duty (0.005% on purchase) + STT charges deducted to-date">Stamp Duty + STT</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-right">Prev Mo. Collected</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-right">Prev Mo. Bounced</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600 text-center">Active / At-risk / Inactive</TableHead>
                                      <TableHead className="font-semibold text-xs text-gray-600">Last Success</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {schemes.map((s, si) => (
                                      <TableRow key={`${c.pan}-sch-${si}`} className="hover:bg-amber-50/30">
                                        <TableCell className="text-xs font-medium text-gray-800">{s.scheme_name}</TableCell>
                                        <TableCell className="text-center font-mono text-xs text-emerald-700">{s.successful_count}</TableCell>
                                        <TableCell className="text-center font-mono text-xs text-rose-600">{s.failed_count}</TableCell>
                                        <TableCell className="text-center font-mono text-xs">
                                          {s.installment_current
                                            ? <>{s.installment_current}{s.installment_total ? `/${s.installment_total}` : ""}</>
                                            : <span className="text-gray-300">—</span>}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs font-semibold text-amber-800">{fmtINR(s.mandate_amount)}</TableCell>
                                        <TableCell className="text-right font-mono text-[11px] text-gray-600">
                                          {fmtINR((s.stamp_duty_total || 0) + (s.stt_total || 0))}
                                          <div className="text-[9px] text-gray-400">
                                            {s.stt_total ? `STT ${fmtINR(s.stt_total)}` : "STT ₹0"}
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-emerald-700">
                                          {fmtINR(s.prev_month_successful_amount)}
                                          <div className="text-[9px] text-emerald-500">{s.prev_month_successful_count} inst</div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs text-rose-600">
                                          {fmtINR(s.prev_month_failed_amount)}
                                          <div className="text-[9px] text-rose-400">{s.prev_month_failed_count} bounced</div>
                                        </TableCell>
                                        <TableCell className="text-center text-xs">
                                          <span className="text-emerald-600 font-semibold">{s.active_mandates}</span>
                                          <span className="text-gray-300"> · </span>
                                          <span className="text-amber-600 font-semibold">{s.at_risk_mandates}</span>
                                          <span className="text-gray-300"> · </span>
                                          <span className="text-gray-500">{s.inactive_mandates}</span>
                                        </TableCell>
                                        <TableCell className="text-xs text-gray-600">
                                          {s.last_successful_date || <span className="text-gray-300">—</span>}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm">
                  <div className="text-gray-600">
                    Page {page} of {totalPages} · {summary.total_clients} clients
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="h-8 px-3 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >Prev</button>
                    <button
                      type="button"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="h-8 px-3 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >Next</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
